import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const Philosophy = () => {
    const { scrollYProgress } = useScroll();
    const y1 = useTransform(scrollYProgress, [0, 1], [0, -100]);
    const y2 = useTransform(scrollYProgress, [0, 1], [0, 100]);

    const fadeIn = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
        }
    };

    const staggerContainer = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        }
    };

    const mathSymbols = [
        { sym: '∫', top: '10%', left: '5%', size: 'text-6xl' },
        { sym: 'Σ', bottom: '15%', right: '10%', size: 'text-5xl' },
        { sym: 'π', top: '20%', right: '20%', size: 'text-4xl' },
        { sym: '√', bottom: '25%', left: '15%', size: 'text-7xl' },
        { sym: 'Δ', top: '40%', left: '2%', size: 'text-3xl' },
    ];

    return (
        <section className="py-32 px-6 relative overflow-hidden bg-[#0a0a0a]">
            {/* Mathematical Floating Elements */}
            {mathSymbols.map((item, i) => (
                <motion.div
                    key={i}
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 0.1 }}
                    animate={{
                        y: [0, -20, 0],
                        rotate: [0, 10, -10, 0]
                    }}
                    transition={{
                        duration: 8 + i,
                        repeat: Infinity,
                        ease: "easeInOut"
                    }}
                    className={`absolute ${item.size} font-serif text-blue-500 pointer-events-none select-none`}
                    style={{ top: item.top, left: item.left, right: item.right, bottom: item.bottom }}
                >
                    {item.sym}
                </motion.div>
            ))}

            {/* Technical HUD Grid Lines */}
            <div className="absolute inset-0 opacity-[0.03] pointer-events-none">
                <div className="absolute top-0 left-1/4 w-[1px] h-full bg-blue-500"></div>
                <div className="absolute top-0 left-2/4 w-[1px] h-full bg-blue-500"></div>
                <div className="absolute top-0 left-3/4 w-[1px] h-full bg-blue-500"></div>
                <div className="absolute top-1/4 left-0 w-full h-[1px] bg-blue-500"></div>
                <div className="absolute top-2/4 left-0 w-full h-[1px] bg-blue-500"></div>
                <div className="absolute top-3/4 left-0 w-full h-[1px] bg-blue-500"></div>
            </div>

            {/* Animated Glow Blobs */}
            <motion.div
                animate={{
                    scale: [1, 1.2, 1],
                    x: [0, 30, 0],
                    y: [0, 20, 0]
                }}
                transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                className="absolute -top-1/4 -right-1/4 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[180px] -z-10"
            ></motion.div>

            <div className="max-w-7xl mx-auto relative">
                <motion.div
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                    className="glass-card p-1 md:p-2   to-transparent border-white/[0.05]  "
                >
                    <div className="  rounded-[2.3rem] p-12 md:p-24 overflow-hidden relative border border-white/10">
                        {/* Interactive Data Lines */}
                        <motion.div
                            animate={{ opacity: [0.2, 0.5, 0.2] }}
                            transition={{ duration: 4, repeat: Infinity }}
                            className="absolute top-12 left-12 flex gap-1"
                        >
                            {[...Array(20)].map((_, i) => (
                                <div key={i} className="w-[2px] h-2 bg-blue-500/30"></div>
                            ))}
                        </motion.div>

                        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-20 items-center">
                            <div className="lg:col-span-7 space-y-12">
                                <motion.div variants={fadeIn} className="space-y-6">
                                    <div className="flex items-center gap-4">
                                        <span className="h-[2px] w-12 bg-blue-600"></span>
                                        <span className="text-blue-500 font-black uppercase tracking-[0.4em] text-[10px]">Scientific Paradigm</span>
                                    </div>
                                    <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-[0.95] text-white">
                                        THE GEOMETRY <br />
                                        <span className="text-gradient">OF UNDERSTANDING.</span>
                                    </h2>
                                    <p className="text-lg text-neutral-400 leading-relaxed font-medium max-w-2xl">
                                        Manoj Sampath methodology focuses on <span className="text-white font-bold border-b-2 border-blue-500/50 pb-1">Cognitive Synthesis</span>.
                                        We translate static formulas into dynamic mental visualizations, unlocking the innate logic of the mathematical universe.
                                    </p>
                                </motion.div>

                                <motion.div variants={fadeIn} className="grid grid-cols-1 sm:grid-cols-2 gap-10">
                                    {[
                                        { title: 'Neural Mapping', desc: 'Recoding mathematical abstractness into structural spatial intuition.' },
                                        { title: 'Axiomatic Logic', desc: 'Deconstructing complex theorems to their most fundamental first principles.' }
                                    ].map((item, i) => (
                                        <div key={i} className="group relative pl-8 border-l border-white/10 hover:border-blue-500 transition-colors duration-500">
                                            <div className="absolute top-0 left-0 w-2 h-2 bg-blue-500 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                            <h4 className="text-white font-black uppercase tracking-widest text-xs mb-3 group-hover:text-blue-400 transition-colors">{item.title}</h4>
                                            <p className="text-sm text-neutral-500 leading-relaxed">{item.desc}</p>
                                        </div>
                                    ))}
                                </motion.div>

                                <motion.div variants={fadeIn}>
                                    <button className="relative group px-10 py-5 bg-white text-black font-black text-[10px] uppercase tracking-[0.2em] rounded-full overflow-hidden transition-all duration-500 hover:pr-14 shadow-[0_20px_40px_rgba(255,255,255,0.1)]">
                                        <span className="relative z-10">Explore the Framework</span>
                                        <span className="absolute right-6 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 translate-x-4 group-hover:translate-x-0 transition-all duration-500">→</span>
                                    </button>
                                </motion.div>
                            </div>

                            <motion.div
                                className="lg:col-span-5 relative"
                                variants={fadeIn}
                            >
                                <div className="relative z-10 rounded-[3.5rem] overflow-hidden border border-white/10 group shadow-2xl">
                                    <motion.div
                                        whileHover={{ scale: 1.05 }}
                                        transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
                                        className="aspect-[4/5] relative"
                                    >
                                        <img
                                            src="https://images.unsplash.com/photo-1509062522246-3755977927d7?q=80&w=2070&auto=format&fit=crop"
                                            className="w-full h-full object-cover grayscale brightness-50 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-1000"
                                            alt="Mathematics Class"
                                        />
                                        <div className="absolute inset-0 bg-blue-600/20 mix-blend-overlay group-hover:opacity-0 transition-opacity"></div>
                                    </motion.div>

                                    {/* HUD Overlay on Image */}
                                    <div className="absolute inset-0 pointer-events-none">
                                        <div className="absolute top-6 left-6 w-12 h-12 border-t-2 border-l-2 border-white/20"></div>
                                        <div className="absolute top-6 right-6 w-12 h-12 border-t-2 border-r-2 border-white/20"></div>
                                        <div className="absolute bottom-6 left-6 w-12 h-12 border-b-2 border-l-2 border-white/20"></div>
                                        <div className="absolute bottom-6 right-6 w-12 h-12 border-b-2 border-r-2 border-white/20"></div>
                                    </div>
                                </div>

                                {/* Floating Data Badges */}
                                <motion.div
                                    style={{ y: y1 }}
                                    className="absolute -top-10 -right-10 glass-card p-6 border-blue-500/30 backdrop-blur-3xl z-20"
                                >
                                    <div className="text-blue-500 font-black text-3xl mb-1 flex items-center gap-2">
                                        <span className="animate-pulse">●</span> 98%
                                    </div>
                                    <div className="text-[8px] text-neutral-500 font-black uppercase tracking-widest">Efficiency Rate</div>
                                </motion.div>

                                <motion.div
                                    style={{ y: y2 }}
                                    className="absolute -bottom-10 -left-10 glass-card p-6 border-white/10 backdrop-blur-3xl z-20"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center text-white scale-x-[-1]">
                                            Σ
                                        </div>
                                        <div>
                                            <div className="text-white font-black text-xl leading-none mb-1">PRO-LEVEL</div>
                                            <div className="text-[8px] text-neutral-500 font-black uppercase tracking-widest">Certified Legacy</div>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Philosophy;
